// ─── Constants ────────────────────────────────────────────────────────────────

export const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
export const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

// Cartoon people avatars via DiceBear Avataaars
export const FOOD_ILLUSTRATIONS = [
  ['person1','https://api.dicebear.com/7.x/avataaars/svg?seed=Felix&backgroundColor=b6e3f4'],
  ['person2','https://api.dicebear.com/7.x/avataaars/svg?seed=Aneka&backgroundColor=ffd5dc'],
  ['person3','https://api.dicebear.com/7.x/avataaars/svg?seed=Milo&backgroundColor=c0aede'],
  ['person4','https://api.dicebear.com/7.x/avataaars/svg?seed=Zara&backgroundColor=d1f4d1'],
  ['person5','https://api.dicebear.com/7.x/avataaars/svg?seed=Oliver&backgroundColor=ffeaa7'],
  ['person6','https://api.dicebear.com/7.x/avataaars/svg?seed=Luna&backgroundColor=fab1a0'],
  ['person7','https://api.dicebear.com/7.x/avataaars/svg?seed=Max&backgroundColor=74b9ff'],
  ['person8','https://api.dicebear.com/7.x/avataaars/svg?seed=Iris&backgroundColor=a29bfe'],
  ['person9','https://api.dicebear.com/7.x/avataaars/svg?seed=Leo&backgroundColor=fd79a8'],
  ['person10','https://api.dicebear.com/7.x/avataaars/svg?seed=Nova&backgroundColor=55efc4'],
  ['person11','https://api.dicebear.com/7.x/avataaars/svg?seed=Ezra&backgroundColor=fdcb6e'],
  ['person12','https://api.dicebear.com/7.x/avataaars/svg?seed=Maya&backgroundColor=e17055'],
];

export const DIETARY_OPTIONS = [
  'Vegetarian', 'Vegan', 'Gluten-free', 'Dairy-free',
  'Halal', 'Kosher', 'Keto', 'Low-carb', 'Nut-free',
];

export const ALLERGEN_OPTIONS = [
  'Peanuts', 'Tree nuts', 'Milk', 'Eggs', 'Fish', 'Shellfish',
  'Wheat', 'Soy', 'Sesame', 'Celery', 'Mustard', 'Sulphites',
];

// ─── Date helpers ─────────────────────────────────────────────────────────────

/** Returns an array of 7 Date objects starting from today + weekOffset weeks. */
export function getWeekDates(offset = 0) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(today);
    d.setDate(today.getDate() + i + offset * 7);
    return d;
  });
}

/** Format a Date as "YYYY-MM-DD". */
export function fmtDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Format a Date as "1 Jan". */
export function dispDate(d) {
  return `${d.getDate()} ${MONTHS[d.getMonth()].slice(0, 3)}`;
}

// ─── Display helpers ──────────────────────────────────────────────────────────

export function catColor(category) {
  return (
    { produce: '#4ade80', meat: '#f87171', dairy: '#facc15', fish: '#60a5fa', pantry: '#c084fc' }[category] ||
    '#94a3b8'
  );
}

/** Format a quantity — integer if whole, one decimal place if not. */
export function fmtQty(q) {
  return q === Math.floor(q) ? String(q) : q.toFixed(1).replace(/\.0$/, '');
}

// ─── Ingredient normalisation ─────────────────────────────────────────────────

const NOISE_WORDS =
  /(?:^|\s)(?:good[ -]quality|good quality|good|quality|fresh|dried|frozen|chopped|diced|sliced|minced|grated|peeled|roughly|finely|thinly|coarsely|lightly|very|large|small|medium|big|flat-leaf|flat leaf|baby|young|ripe|raw|cooked|boneless|skinless|free-range|organic|british|italian|english|french|chinese|thai|indian|a|an|the|some|few|little|thick|thin|whole|halved|quartered|trimmed|washed)(?:\s|$)/gi;

const ALIASES = {
  'Fresh Coriander': 'Coriander',
  'Flat-Leaf Parsley': 'Flat Leaf Parsley',
  'Curly Parsley': 'Parsley',
  'Spring Onion': 'Spring Onions',
  Scallion: 'Spring Onions',
  Scallions: 'Spring Onions',
  Chilli: 'Red Chilli',
  Chili: 'Red Chilli',
  'Red Chilli Pepper': 'Red Chilli',
  'Bell Pepper': 'Bell Peppers',
  Zucchini: 'Courgette',
  Eggplant: 'Aubergine',
  'Chick Peas': 'Chickpeas',
  'Garbanzo Beans': 'Chickpeas',
  'Natural Yogurt': 'Natural Yoghurt',
  'Greek Yogurt': 'Greek Yoghurt',
  'Heavy Cream': 'Double Cream',
  'Tinned Tomatoes': 'Chopped Tomatoes',
  'Canned Tomatoes': 'Chopped Tomatoes',
  'Cherry Tomato': 'Cherry Tomatoes',
  'Halloumi Cheese': 'Halloumi',
  'Mozzarella Cheese': 'Mozzarella',
  'Parmesan Cheese': 'Parmesan',
  'Cheddar Cheese': 'Cheddar',
  'Feta Cheese': 'Feta',
  'Chicken Breasts': 'Chicken Breast',
  'Chicken Thigh': 'Chicken Thighs',
  'Chicken Breast Strips': 'Chicken Breast',
  'Extra Virgin Olive Oil': 'Olive Oil',
  'Penne Pasta': 'Penne',
  'White Onion': 'Onion',
  'Brown Onion': 'Onion',
  'Garlic Clove': 'Garlic',
  'Garlic Cloves': 'Garlic',
  'Root Ginger': 'Fresh Ginger',
  'Fresh Root Ginger': 'Fresh Ginger',
  Ginger: 'Fresh Ginger',
  Tomato: 'Tomatoes',
  Egg: 'Eggs',
  'Unsalted Butter': 'Butter',
  'Salted Butter': 'Butter',
  'Ground Black Pepper': 'Black Pepper',
  Pepper: 'Black Pepper',
};

function toTitleCase(str) {
  return str
    .toLowerCase()
    .split(' ')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
    .replace(/ Of /g, ' of ')
    .replace(/ And /g, ' and ')
    .trim();
}

export function normaliseIngredient(ing) {
  let { name, qty, unit } = ing;

  // e.g. "x 225g blocks of halloumi" with qty=2 → qty=450, unit=g
  const embeddedMatch = name.match(/^(?:x\s*)?(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb)\s+(.+)$/i);
  if (embeddedMatch) {
    const embQty = parseFloat(embeddedMatch[1]);
    const embUnit = embeddedMatch[2].toLowerCase();
    const isBulkUnit = unit === '' ||
      /^(block|pack|tin|can|fillet|rasher|slice|piece|bag|sachet|bunch|sprig|clove)s?$/i.test(unit);
    qty = qty > 1 && isBulkUnit ? qty * embQty : embQty;
    unit = embUnit;
    name = embeddedMatch[3];
  }

  // e.g. "2 x 125g mozzarella"
  const multiMatch = name.match(/^(\d+)\s*x\s*(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb)\s+(.+)$/i);
  if (multiMatch) {
    qty = parseInt(multiMatch[1]) * parseFloat(multiMatch[2]);
    unit = multiMatch[3].toLowerCase();
    name = multiMatch[4];
  }

  // e.g. "400g chopped tomatoes"
  if (!embeddedMatch && !multiMatch) {
    const leadingMatch = name.match(/^(\d+(?:\.\d+)?)\s*(g|kg|ml|l|oz|lb|tsp|tbsp|cups?)\s+(.+)$/i);
    if (leadingMatch) {
      const potUnit = leadingMatch[2].toLowerCase();
      if (potUnit.length > 1) {
        qty = parseFloat(leadingMatch[1]);
        unit = potUnit;
        name = leadingMatch[3];
      }
    }
  }

  name = name.replace(NOISE_WORDS, ' ').replace(/\s+/g, ' ').trim();
  name = name.replace(/,.*$/, '').trim();
  name = name.replace(/\([^)]*\)/g, '').trim();
  name = toTitleCase(name);
  name = ALIASES[name] || name;

  return { ...ing, name, qty, unit };
}

// ─── Calendar (.ics) export ───────────────────────────────────────────────────

function fmtIcsDate(dateStr, timeHour = 19) {
  const d = new Date(`${dateStr}T00:00:00`);
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(d.getDate())}T${pad(timeHour)}0000`;
}

function escapeIcs(str) {
  return String(str || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '');
}

function wrapIcsLine(line) {
  const out = [];
  while (line.length > 75) {
    out.push(line.slice(0, 75));
    line = ' ' + line.slice(75);
  }
  out.push(line);
  return out.join('\r\n');
}

function generateIcs(mealPlan, recipes, servingsOvr) {
  const dtstamp = new Date().toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Platewise//Meal Planner//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'X-WR-CALNAME:Platewise Meal Plan',
  ];

  Object.entries(mealPlan).forEach(([dateStr, recipeIds]) => {
    recipeIds.forEach((rid, idx) => {
      const r = recipes.find((x) => x.id === rid);
      if (!r) return;
      const servings = servingsOvr[rid] || r.servings;
      const scale = servings / r.servings;
      const ingList = r.ingredients
        .map((i) => `- ${fmtQty(i.qty * scale)}${i.unit ? ' ' + i.unit : ''} ${i.name}`)
        .join('\n');
      const methodList = r.method.map((s, i) => `${i + 1}. ${s}`).join('\n');
      const description = escapeIcs(
        `Serves: ${servings} | Time: ${r.time} mins | Cuisine: ${r.cuisine}\n\nINGREDIENTS:\n${ingList}\n\nMETHOD:\n${methodList}`,
      );
      const startHour = 19 + idx;
      lines.push('BEGIN:VEVENT');
      lines.push(`UID:platewise-${rid}-${dateStr}-${Date.now()}@platewise.app`);
      lines.push(`DTSTAMP:${dtstamp}`);
      lines.push(`DTSTART;TZID=Europe/London:${fmtIcsDate(dateStr, startHour)}`);
      lines.push(`DTEND;TZID=Europe/London:${fmtIcsDate(dateStr, startHour + 1)}`);
      lines.push(wrapIcsLine(`SUMMARY:${escapeIcs('🍽 ' + r.title)}`));
      lines.push(wrapIcsLine(`DESCRIPTION:${description}`));
      lines.push('STATUS:CONFIRMED');
      lines.push('TRANSP:OPAQUE');
      lines.push('END:VEVENT');
    });
  });

  lines.push('END:VCALENDAR');
  return lines.join('\r\n') + '\r\n';
}

export function downloadIcs(mealPlan, recipes, servingsOvr) {
  const ics = generateIcs(mealPlan, recipes, servingsOvr);
  const blob = new Blob([ics], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'platewise-meal-plan.ics';
  a.click();
  URL.revokeObjectURL(url);
}

// ─── Recipe PDF / print ───────────────────────────────────────────────────────

export function shareRecipePdf(recipe, servings) {
  const scale = servings / recipe.servings;
  const ingList = recipe.ingredients
    .map(
      (i) =>
        `<tr><td style="padding:6px 8px;border-bottom:1px solid #e8ddd4">${i.name}</td>` +
        `<td style="padding:6px 8px;border-bottom:1px solid #e8ddd4;text-align:right;font-weight:600;color:#5c3d2e">${fmtQty(i.qty * scale)} ${i.unit}</td></tr>`,
    )
    .join('');
  const methodList = recipe.method
    .map((s) => `<li style="margin-bottom:12px;line-height:1.7">${s}</li>`)
    .join('');

  const html = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${recipe.title}</title>
<style>
  *{box-sizing:border-box;margin:0;padding:0}
  body{font-family:Georgia,serif;max-width:680px;margin:0 auto;padding:40px 32px;color:#2d1f14}
  h1{font-size:28px;border-bottom:3px solid #c4674a;padding-bottom:10px;margin-bottom:8px}
  .meta{color:#7a6256;font-size:13px;margin-bottom:20px}
  img{width:100%;max-height:260px;object-fit:cover;border-radius:8px;margin-bottom:20px}
  h2{font-size:14px;color:#c4674a;margin:20px 0 8px;text-transform:uppercase;letter-spacing:0.5px}
  table{width:100%;border-collapse:collapse}
  td{padding:6px 8px;border-bottom:1px solid #e8ddd4;font-size:13px}
  td:last-child{text-align:right;font-weight:600;color:#5c3d2e}
  ol{padding-left:20px}li{font-size:13px;margin-bottom:10px;line-height:1.7}
  footer{margin-top:24px;color:#bbb;font-size:10px;border-top:1px solid #e8ddd4;padding-top:10px}
  @media print{@page{margin:1.5cm}}
</style></head><body>
${recipe.image ? `<img src="${recipe.image}" crossorigin="anonymous">` : ''}
<h1>${recipe.title}</h1>
<div class="meta">&#9201; ${recipe.time} min &middot; ${servings} servings &middot; ${recipe.cuisine || ''}</div>
<h2>Ingredients</h2><table>${ingList}</table>
<h2>Method</h2><ol>${methodList}</ol>
<footer>Created with Platewise</footer>
</body></html>`;

  const win = window.open('', '_blank', 'width=800,height=900');
  if (!win) {
    const blob = new Blob([html], { type: 'text/html' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `${recipe.title.replace(/[^a-z0-9]/gi, '-')}.html`;
    a.click();
    return;
  }
  win.document.write(html);
  win.document.close();
  win.addEventListener('load', () => setTimeout(() => { win.focus(); win.print(); }, 400));
}
